import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { providersApi } from '@/api/services/providers';
import type {
  CreateProviderDto,
  ProviderAccountFormDto,
  UpdateProviderDto,
} from '@/api/types/providers';

export const providerKeys = {
  all: ['providers'] as const,
  list: ['providers', 'list'] as const,
  types: ['providers', 'types'] as const,
  accountLookup: ['providers', 'account-lookup'] as const,
  account: (providerId: string) => ['providers', 'account', providerId] as const,
};

export function useProviderTypes() {
  return useQuery({
    queryKey: providerKeys.types,
    queryFn: () => providersApi.getProviderTypes(),
  });
}

export function useCreateProvider() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateProviderDto) => providersApi.create(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: providerKeys.list }),
  });
}

export function useUpdateProvider() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateProviderDto }) =>
      providersApi.update(id, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: providerKeys.list }),
  });
}

export function useDeleteProvider() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => providersApi.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: providerKeys.list }),
  });
}

export function useAccountLookup() {
  return useQuery({
    queryKey: providerKeys.accountLookup,
    queryFn: () => providersApi.getAccountLookup(),
  });
}

export function useProviderAccount(providerId: string, enabled = true) {
  return useQuery({
    queryKey: providerKeys.account(providerId),
    queryFn: () => providersApi.getAccount(providerId),
    enabled: enabled && !!providerId,
  });
}

export function useCreateAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ProviderAccountFormDto) => providersApi.createAccount(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: providerKeys.all }),
  });
}

export function useUpdateAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: ProviderAccountFormDto }) =>
      providersApi.updateAccount(id, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: providerKeys.all }),
  });
}
